import React, { useEffect, useRef, useState } from 'react';
import { formatDate } from '../utils';
import { isCanonicalDate, isValidDateParts } from '../utils/calendarDate';

export interface DateInputValidity {
  rawValue: string;
  canonicalValue: string;
  state: 'empty' | 'incomplete' | 'invalid' | 'valid';
}

interface DateInputProps {
  id?: string;
  name?: string;
  ariaLabel?: string;
  'aria-label'?: string;
  className?: string;
  value?: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  strict?: boolean;
  onValidityChange?: (validity: DateInputValidity) => void;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

type NormalizedDateInput = {
  display: string;
  isoValue: string;
  isComplete: boolean;
  isEmpty: boolean;
  isValidComplete: boolean;
};

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const formatTypedDate = (input: string) => {
  const digits = input.replace(/\D/g, '').slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
  return parts.join('.');
};

const displayDateToIso = (displayValue: string) => {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(displayValue);
  if (!match) return '';

  const [, day, month, year] = match;
  return isValidDateParts(Number(day), Number(month), Number(year)) ? `${year}-${month}-${day}` : '';
};

const isoDateToNativeValue = (value?: string) => {
  const match = value?.match(ISO_DATE_PATTERN);
  if (!match) return '';

  const [, year, month, day] = match;
  return isValidDateParts(Number(day), Number(month), Number(year)) ? `${year}-${month}-${day}` : '';
};

const normalizeDateInput = (input: string): NormalizedDateInput => {
  const display = formatTypedDate(input);
  const isEmpty = display.length === 0;
  const isComplete = display.length === 10;
  const isoValue = isComplete ? displayDateToIso(display) : '';

  return {
    display,
    isoValue,
    isComplete,
    isEmpty,
    isValidComplete: isComplete && Boolean(isoValue),
  };
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'date';

/**
 * Displays TimeTracking local dates in the app convention dd.MM.yyyy while
 * preserving yyyy-MM-dd values for existing component state and API requests.
 * A native date input is still present and opened by an explicit button so
 * users keep the calendar picker without leaking yyyy-MM-dd into the visible
 * text field.
 */
const DateInput: React.FC<DateInputProps> = ({
  id,
  name,
  ariaLabel,
  'aria-label': ariaLabelAttribute,
  className = 'form-control',
  value = '',
  onChange,
  required,
  disabled,
  placeholder = 'dd.MM.yyyy',
  strict = false,
  onValidityChange,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}) => {
  const textInputLabel = ariaLabelAttribute ?? ariaLabel;
  const accessibleName = textInputLabel ?? name ?? id ?? 'Date';
  const calendarInputId = `${id ?? slugify(accessibleName)}-calendar-picker`;
  const wrapperClassName = [
    'date-input-wrapper',
    ...className.split(/\s+/).filter(cssClass => cssClass.startsWith('filter-')),
  ].join(' ');
  const nativeDateInputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState(formatDate(value));
  const [hasInvalidCompleteInput, setHasInvalidCompleteInput] = useState(false);
  const nativeValue = strict && value && !isCanonicalDate(value) ? '' : isoDateToNativeValue(value);
  const emittedValue = useRef<string | null>(null);
  const validityCallback = useRef(onValidityChange);
  validityCallback.current = onValidityChange;

  const reportValidity = (normalized: NormalizedDateInput) => {
    validityCallback.current?.({
      rawValue: normalized.display,
      canonicalValue: normalized.isoValue,
      state: normalized.isEmpty ? 'empty' : normalized.isValidComplete ? 'valid' : normalized.isComplete ? 'invalid' : 'incomplete',
    });
  };

  useEffect(() => {
    // A strict invalid edit emits '', but its raw text must survive the parent echo.
    if (strict && emittedValue.current === value) {
      emittedValue.current = null;
      return;
    }
    setDisplayValue(formatDate(value));
    setHasInvalidCompleteInput(strict && !!value && !isCanonicalDate(value));
    const normalized = normalizeDateInput(formatDate(value));
    const validValue = normalized.isValidComplete && (!strict || isCanonicalDate(value));
    validityCallback.current?.({ rawValue: normalized.display, canonicalValue: validValue ? normalized.isoValue : '',
      state: !value ? 'empty' : validValue ? 'valid' : 'invalid' });
  }, [value, strict]);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeDateInput(event.target.value);
    setDisplayValue(normalized.display);
    setHasInvalidCompleteInput(normalized.isComplete && !normalized.isValidComplete);
    reportValidity(normalized);

    if (strict) {
      emittedValue.current = normalized.isoValue;
      onChange(normalized.isoValue);
      return;
    }

    if (normalized.isEmpty) {
      onChange('');
      return;
    }

    if (normalized.isValidComplete) {
      onChange(normalized.isoValue);
    }
  };

  const handleNativeDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isoValue = strict && !isCanonicalDate(event.target.value) ? '' : isoDateToNativeValue(event.target.value);
    setHasInvalidCompleteInput(false);
    emittedValue.current = isoValue;
    reportValidity(normalizeDateInput(formatDate(isoValue)));

    if (!isoValue) {
      setDisplayValue('');
      onChange('');
      return;
    }

    setDisplayValue(formatDate(isoValue));
    onChange(isoValue);
  };

  const handleBlur = () => {
    if (strict) return;
    if (displayValue && !displayDateToIso(displayValue)) {
      setDisplayValue(formatDate(value));
      setHasInvalidCompleteInput(false);
    }
  };

  const openNativePicker = () => {
    const input = nativeDateInputRef.current;
    if (!input || disabled) return;

    const inputWithPicker = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof inputWithPicker.showPicker === 'function') {
      inputWithPicker.showPicker();
      return;
    }

    input.focus();
    input.click();
  };

  return (
    <span className={wrapperClassName}>
      <input
        id={id}
        name={name}
        aria-label={textInputLabel}
        aria-invalid={invalid || hasInvalidCompleteInput || undefined}
        aria-describedby={describedBy}
        className={className}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        maxLength={10}
        value={displayValue}
        onChange={handleTextChange}
        onBlur={handleBlur}
        required={required}
        disabled={disabled}
      />
      <button
        type="button"
        className="date-input-picker-button"
        aria-label={`Open ${accessibleName} calendar`}
        aria-controls={calendarInputId}
        onClick={openNativePicker}
        disabled={disabled}
      >
        <span aria-hidden="true">📅</span>
      </button>
      <input
        ref={nativeDateInputRef}
        id={calendarInputId}
        aria-label={`${accessibleName} calendar picker`}
        className="date-input-native-picker"
        type="date"
        value={nativeValue}
        onChange={handleNativeDateChange}
        tabIndex={-1}
        required={required}
        disabled={disabled}
      />
    </span>
  );
};

export default DateInput;
